import React, { useState, useRef, useEffect } from "react";
import { SlIcon } from "@shoelace-style/shoelace/dist/react";
import { alphaformatDateTime } from "../../../utils/dateFormat.js";
import { ConfirmationModal } from "../../../shared/ConfirmationModal.js";
import ContentWriterAssignmentModal from "../components/ContentWriterAssignmentModal.js";
import ContentDetailsCell from "../components/ContentDetailsCell.js";
import GoLiveButton from "../components/GoLiveButton.js";
import OrderDetails from "../components/OrderDetails.js";

export const renderSalesPersonCell = (value, row) => {
  let startDateTime, endDateTime;
  {
    let { dateStr, timeStr } = alphaformatDateTime(row.creative_start_date);
    startDateTime = dateStr + " " + timeStr;
  }
  {
    const { dateStr, timeStr } = alphaformatDateTime(row.creative_start_date);
    endDateTime = dateStr + " " + timeStr;
  }
  return (
    <div>
      <div className="flex gap-1 items-center  font-medium">
        <SlIcon library="lucide" name="user" style={{ fontSize: "14px" }}></SlIcon>
        {value.sales_person_name}
      </div>
      <div className="flex gap-1 items-center  text-gray-600">
        <SlIcon library="lucide" name="phone" style={{ fontSize: "12px" }}></SlIcon>
        {value.mobile}
      </div>
      <div className="flex flex-col">
        <span className="mb-2 px-2 py-1 bg-green-300 rounded-lg">start : {startDateTime}</span>
        <span className="px-2 py-1 bg-red-300 rounded-lg">end : {endDateTime}</span>
      </div>
    </div>
  );
};

export const renderDateTimeCell = (value) => {
  if (!value || value === -1) return "-";
  const { dateStr, timeStr } = alphaformatDateTime(value);
  return (
    <>
      <div className="font-bold">{dateStr}</div>
      <div className="font-bold">{timeStr}</div>
    </>
  );
};

const AssignToCell = ({ row, isPending, designers, handleDesignerChange }) => {
  const [editing, setEditing] = useState(false);
  const [selectedDesigner, setSelectedDesigner] = useState(-1);
  const [showModal, setShowModal] = useState(false);
  const [pendingDesigner, setPendingDesigner] = useState(null);
  const cellRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    function handleClickOutside(event) {
      if (cellRef.current && !cellRef.current.contains(event.target)) {
        setEditing(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editing]);

  const handleSelectChange = (e) => {
    const newDesignerId = e.target.value;
    const selectedDesignerObj = designers.find((d) => d?.designer_id === parseInt(newDesignerId));
    if (!newDesignerId) return;
    setPendingDesigner({ id: newDesignerId, name: selectedDesignerObj?.name, obj: selectedDesignerObj });
    setShowModal(true);
  };

  const handleModalConfirm = () => {
    if (pendingDesigner) {
      setSelectedDesigner(pendingDesigner.id);
      handleDesignerChange(row?.creative_id, pendingDesigner.id, pendingDesigner.name);
    }
    setShowModal(false);
    setEditing(false);
    setPendingDesigner(null);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setEditing(false);
    setPendingDesigner(null);
  };

  return (
    <div ref={cellRef} className="flex items-center gap-2">
      {isPending && !editing ? (
        <sl-tooltip content="Edit Designer">
          <sl-icon-button library="lucide" name="edit" onClick={() => setEditing(true)}></sl-icon-button>
        </sl-tooltip>
      ) : null}

      {editing ? (
        <>
          <select className="border p-1 rounded" value={selectedDesigner} onChange={handleSelectChange}>
            <option value="">Select Designer</option>
            {Array.isArray(designers) && designers.length > 0 ? (
              designers.map((designer) => (
                <option key={designer?.designer_id} value={designer?.designer_id}>
                  {designer?.name}
                </option>
              ))
            ) : (
              <option value="" disabled>
                No designers
              </option>
            )}
          </select>
          <ConfirmationModal open={showModal} message={`Are you sure you want to assign to ${pendingDesigner?.name}?`} onConfirm={handleModalConfirm} onCancel={handleModalCancel} />
        </>
      ) : (
        <span>{row.designer_name || <span className="text-gray-400">Unassigned</span>}</span>
      )}
    </div>
  );
};

export const renderAssignToCell = (row, designers, isPending, handleDesignerChange) => {
  return (
    <div>
      <AssignToCell row={row} isPending={isPending} designers={designers} handleDesignerChange={handleDesignerChange} />
    </div>
  );
};

const AssignContentWriterCell = ({ row, isPending, contentWriters, handleContentWriterChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [currentContentWriter, setCurrentContentWriter] = useState(null);

  const handleAssignClick = () => {
    setCurrentContentWriter(
      row.content_writer_name
        ? {
            content_writer_id: row.content_writer_id,
            name: row.content_writer_name,
          }
        : null
    );
    setShowModal(true);
  };

  const handleModalConfirm = async (data) => {
    try {
      await handleContentWriterChange(data.content_id, data.creative_id, data.content_writer_id, data.content_writer_name, data.description);
    } catch (error) {
      console.error("Error in content writer assignment:", error);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button onClick={handleAssignClick} className="py-1 mx-1 flex items-center justify-center gap-2">
        <SlIcon library="lucide" name="edit-3" />
        {row.content_writer_name ? row.content_writer_name : "Assign Content Writer"}
      </button>

      <ContentWriterAssignmentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleModalConfirm}
        contentWriters={contentWriters}
        currentContentWriter={currentContentWriter}
        currentDescription={row.rough_contest_description || ""}
        creativeId={row.creative_id}
        content_id={row.content_id}
      />
    </div>
  );
};

export const renderAssignContentWriterCell = (row, contentWriters, isPending, handleContentWriterChange) => {
  return (
    <div className="flex flex-col items-start justify-start gap-2">
      <ContentDetailsCell creative_id={row.creative_id} adDetails={row} />
      <AssignContentWriterCell row={row} isPending={isPending} contentWriters={contentWriters} handleContentWriterChange={handleContentWriterChange} />
    </div>
  );
};

export const renderPaymentStatusCell = (value) => <span className={`px-2 py-1  rounded-full  font-medium ${value === "Done" ? "bg-done text-done_text" : "bg-pending text-pending_text"}`}>{value}</span>;

export const renderLiveStatusCell = (row, handleGoToLive, goLiveLoadingIdx, rowIdx) => {
  return <GoLiveButton row={row} handleGoToLive={handleGoToLive} goLiveLoadingIdx={goLiveLoadingIdx} rowIdx={rowIdx} />;
};

const renderContent = (creative_id, fetchContent, row) => {
  return <ContentDetailsCell creative_id={creative_id} adDetails={row} />;
};
export const renderDesignStatusCell = (statusCode) => {
  let color = "bg-gray-100 text-gray-800";
  let label = "Unknown";

  switch (statusCode) {
    case 1:
      label = "Draft";
      color = "bg-gray-100 text-gray-800";
      break;
    case 2:
      label = "Saved";
      color = "bg-blue-100 text-blue-800";
      break;
    case 3:
      label = "Picked";
      color = "bg-pending text-pending_text";
      break;
    case 4:
      label = "Completed";
      color = "bg-done text-done_text";
      break;
    case 5:
      label = "Rejected";
      color = "bg-reject text-reject_text";
      break;
    case 6:
      label = "Accepted";
      color = "bg-client_approve text-client_approve_text";
      break;
    case 7:
      label = "Payment Success";
      color = "bg-green-100 text-green-800";
      break;
    case 8:
      label = "Live";
      color = "bg-done text-done_text";
      break;
    case 9:
      label = "Completed";
      color = "bg-not_pickup text-not_pickup_text";
      break;
    default:
      label = "Unknown";
      color = "bg-gray-100 text-gray-800";
  }
  return (
    <div className="flex items-center gap-2 w-full justify-center">
      <span className={`px-2 py-1 rounded-full  font-medium ${color}`}>{label}</span>
    </div>
  );
};
export const renderCreativeName = (row) => {
  return (
    <div className="flex flex-col gap-1 justify-start items-start">
      <span className="font-bold bg-gray-200 shadow-md px-1 py-1">{row.creative_name}</span>
      {row.brand_name && <span className="bg-yellow-300 px-1 rounded-lg">brand name : {row.brand_name}</span>}
      {row.client_name && <span className="bg-blue-300 px-1 rounded-lg">client name : {row.client_name}</span>}
    </div>
  );
};
export const renderAdType = (row) => {
  return (
    <div className="flex flex-col justify-start items-start">
      <div>
        <span className="bg-gray-200 px-1 py-1 mr-1 rounded-md"> creative type : </span> {row.ad_creative_sub_type}
      </div>
      <hr className="w-full border-t border-gray-300" />
      <div>
        <span className="bg-gray-200 px-1 py-1 mr-1 rounded-md">Ad Type:</span> {row.type}
      </div>
    </div>
  );
};
export const renderOrderDetails = (row) => {
  return <OrderDetails creative_id={row.creative_id} creative_name={row.creative_name} />;
};
export const renderDefaultCell = (value) => <span className="">{value}</span>;

export const createCellRenderer = () => {
  return (key, value, row, designers, contentWriters, isPending, handleDesignerChange, handleContentWriterChange, handleGoToLive, goLiveLoadingIdx, rowIdx, fetchContent) => {
    switch (key) {
      case "sales_person":
        return renderSalesPersonCell(value, row);
      case "designer_name":
        return renderAssignToCell(row, designers, isPending, handleDesignerChange);
      case "content_writer_name":
        return renderAssignContentWriterCell(row, contentWriters, isPending, handleContentWriterChange);
      case "paymentStatus":
        return renderPaymentStatusCell(value);
      case "live_status":
        return renderLiveStatusCell(row, handleGoToLive, goLiveLoadingIdx, rowIdx);
      case "status":
        return renderDesignStatusCell(value);
      case "created_at":
        return renderDateTimeCell(value);
      case "creative_name":
        return renderCreativeName(row);
      case "ad_creative_sub_type":
        return renderAdType(row);
      case "content":
        return renderContent(row.creative_id, fetchContent, row);
      case "order_details":
        return renderOrderDetails(row);
      default:
        return renderDefaultCell(value);
    }
  };
};
